import styled, { css } from 'styled-components';

import { Input, mediaBreakpointMixin, Page, PageContent, Text, token } from '@zillow/constellation';

import { SETTINGS } from '../constants/settings';

const centerMixin = `
 display: flex;
 justify-content: center;
 align-items: center;
`;

const getBackgroundColorStyle = () => {
  return css`
    background-color: ${token('colors.white')};
  `;
};

export const getLeftVerticalNavWidthStyle = ({ hideHeaderNavigation = false }: { hideHeaderNavigation: boolean }) => {
  if (hideHeaderNavigation) {
    return css`
      width: 0;
    `;
  }

  return css`
    width: ${SETTINGS.LEFT_NAV_WIDTH}px;
  `;
};

export const getBodyContainerMarginLeftStyle = ({
  hideHeaderNavigation = false,
}: {
  hideHeaderNavigation: boolean;
}) => {
  if (hideHeaderNavigation) {
    return css`
      margin-left: 0;
    `;
  }

  return css`
    margin-left: ${SETTINGS.LEFT_NAV_WIDTH}px;
  `;
};

export const StyledAdminContainer = styled.div<{ isFullOpacity?: boolean }>`
  display: flex;
  flex-direction: row;

  position: fixed;
  left: 0;
  width: 100%;
  overflow: hidden;
  z-index: ${token('zIndices.toast')};

  opacity: 0.1;
  transition: opacity 0.5s ease;
  &:hover {
    opacity: 1;
  }

  cursor: auto;
`;

export const StyledSpinnerContainerPage = styled(Page)`
  display: flex;
  align-items: center;
  align-self: center;
  justify-content: center;

  position: fixed;
  width: 100%;
  height: 100%;
`;

export const StyledPageContent = styled(PageContent)`
  display: flex;
  align-items: center;
  justify-content: center;

  height: 100%;
  width: 100%;
`;

export const StyledDisplayUntilMinWidthXL = styled.div`
  display: block;

  /* 1024 */
  @media ${mediaBreakpointMixin('xl')} {
    display: none;
  }
`;

export const StyledLayoutContainer = styled.div<{ isHorizontal: boolean; isMobileMenuExpanded?: boolean }>`
  display: flex;
  flex: 1;
  width: 100%;
  height: 100%;

  /* render legacy layout for less than XL breakpoint */
  flex-direction: column;

  position: ${({ isMobileMenuExpanded = false }) => (isMobileMenuExpanded ? 'fixed' : 'relative')};

  z-index: 1000;

  /* render layout based on prop for larger than XL breakpoint */
  @media (min-width: ${`${SETTINGS.BREAKPOINTS_XL}px`}) {
    flex-direction: ${({ isHorizontal = false }) => (isHorizontal ? 'row' : 'column')};
  }
`;

export const getBodyContainerStyle = ({
  hideHeaderNavigation = false,
  isHorizontal = false,
}: {
  hideHeaderNavigation: boolean;
  isHorizontal: boolean;
}) => {
  if (isHorizontal) {
    return css`
      ${centerMixin}
      flex-direction: column;
      flex-grow: 1;
      justify-content: flex-start;

      ${getBodyContainerMarginLeftStyle({ hideHeaderNavigation })}
      overflow-y: auto;
    `;
  }

  return css`
    height: 100%;
    overflow-y: auto;
  `;
};

export const StyledBodyContainer = styled.div<{ hideHeaderNavigation: boolean; isHorizontal: boolean }>`
  height: 100%;
  overflow-y: auto;

  @media (min-width: ${`${SETTINGS.BREAKPOINTS_XL}px`}) {
    ${({ hideHeaderNavigation = false, isHorizontal = false }) =>
      getBodyContainerStyle({ hideHeaderNavigation, isHorizontal })}
  }
`;

export const StyledBodyContentContainer = styled.div`
  width: 100%;
  height: 100%;
`;

export const StyledAppLayoutContainer = styled.div<{
  fullViewportHeight?: boolean;
  offsetHeight?: number;
  setBackgroundColor?: boolean;
}>`
  // Set border-box on container and have all descendants inherit it
  box-sizing: border-box;

  display: flex;
  flex-direction: column;

  width: 100%;
  height: 100%;

  overflow: hidden;

  ${({ setBackgroundColor = false }) => setBackgroundColor && getBackgroundColorStyle}

  // Apply box-sizing: border-box to all children elements
  // This ensures consistent box model behavior throughout the component tree
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
`;

export const StyledCloseBtnContainer = styled.div`
  display: flex;
  align-self: center;

  position: absolute;
  right: 0;
  margin-right: 16px;
  cursor: pointer;
  z-index: 20;
`;

export const StyledHideUntilMinWidthXLLarge = styled.div`
  display: none;

  @media (min-width: ${`${SETTINGS.BREAKPOINTS_XL}px`}) {
    display: block;
  }
`;

export const StyledAdminModalBody = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export const StyledAdminModalBodyHeading = styled(Text)`
  font-size: ${token('fontSizes.h5')}px;
`;

export const StyledAdminModalInputContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;

  margin-bottom: 16px;
`;

export const StyledAdminModalInput = styled(Input)`
  text-align: center;
  font-size: ${token('fontSizes.h4')}px;
  width: 60px;
`;

export const StyledWarningText = styled(Text)`
  max-width: 270px;
`;

export const StyledAdminModalInputAdornment = styled(Text)`
  font-size: ${token('fontSizes.h5')}px;
`;
